import React, { useEffect, useMemo, useState } from 'react';
import { assignmentService, chartService, simulationService } from 'services';
import { ELSModalServiceType } from 'models/els';
import { FragmentType } from 'models/enum';
import { calculateAge, formatDate, getSimulationEhrDateOfBirth, normalizeMonthDayDob, toIsoDob, toMoment } from 'helpers/datetime.helper';
import {
  BarcodeFragment,
  buildPharmacyPatientContext,
  extractMedicationOptionsFromMetadata,
  getPharmacyMetadataOptionsForField,
  generateBarcodeValue,
  loadDirectContentAllergies,
  loadDirectContentMedicationOptions,
  normalizeMedicationIdentity,
  normalizeMedicationOptionLabel,
  normalizeFromFragments,
  extractMedicationFormFromOrderLabel,
  validateMedicationDoseInput,
  validateMedicationFormInput
} from 'helpers/barcode.helper';
import bwipjs from 'bwip-js';
import { ELSButton, ELSCheckBox, ELSDropDownSearch, ELSFlex, ELSFlexItem, ELSIcon, ELSTextBox } from 'components/common/els';
import { BARCODE_CHART_PROP, BARCODE_MODAL_ID, CODE39_MAP, Locales, NAV_ID } from 'constants/app.constant';
import {
  BarcodeDetailsState,
  BarcodeModalContentProps,
  DropDownItem,
  formatMedicationName,
  MedicationItem
} from './barcode-modal.types';
import { BarcodePrintPreview } from './BarcodePrintPreview';
import './barcode-modal.scss';

const emptyState: BarcodeDetailsState = {
  patient: {
    name: '--',
    mrn: '--',
    age: '--',
    patientId: '--',
    allergies: 'NA',
    dateOfBirth: ''
  },
  medications: []
};

const BarcodeFromValue = ({ value, className = '' }: { value?: string; className?: string }) => {
  const normalized = (value || '').toUpperCase().trim();

  if (!normalized) {
    return <div className={`barcode-modal__barcode-image ${className}`} aria-hidden="true" />;
  }

  const encoded = `*${normalized}*`;
  const modules: { isBar: boolean; width: number }[] = [];
  let totalWidth = 0;

  Array.from(encoded).forEach((char, charIndex) => {
    const pattern = CODE39_MAP[char] || CODE39_MAP['-'];
    Array.from(pattern).forEach((token, tokenIndex) => {
      const width = token === 'w' ? 3 : 1;
      const isBar = tokenIndex % 2 === 0;
      modules.push({ isBar, width });
      totalWidth += width;
    });
    if (charIndex < encoded.length - 1) {
      modules.push({ isBar: false, width: 1 });
      totalWidth += 1;
    }
  });

  let cursor = 0;
  return (
    <svg className={className} viewBox={`0 0 ${totalWidth} 48`} preserveAspectRatio="none" role="img" aria-label={`Barcode ${normalized}`}>
      <rect x="0" y="0" width={totalWidth} height="48" fill="#fff" />
      {modules.map((module, index) => {
        const x = cursor;
        cursor += module.width;
        if (!module.isBar) {
          return null;
        }
        return <rect key={`${normalized}-${index}`} x={x} y="0" width={module.width} height="48" fill="#111" />;
      })}
    </svg>
  );
};

const DataMatrixFromValue = ({ value, className = '' }: { value?: string; className?: string }) => {
  const [svgContent, setSvgContent] = useState('');

  useEffect(() => {
    if (!value) {
      setSvgContent('');
      return;
    }
    try {
      const svg = bwipjs.toSVG({
        bcid: 'datamatrix',
        text: value,
        scale: 3
      });
      setSvgContent(svg);
    } catch (_error) {
      setSvgContent('');
    }
  }, [value]);

  if (!svgContent) {
    return <div className="barcode-modal__qr-placeholder" aria-label={`Data Matrix ${value}`} />;
  }

  return (
    <div
      className={className}
      aria-label={`Data Matrix ${value}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};


export const BarcodeModalContent: React.FC<BarcodeModalContentProps> = ({ assignmentId, simulationEhrId, userId, modalService, modalId }) => {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [details, setDetails] = useState<BarcodeDetailsState>(emptyState);
  const [searchInput, setSearchInput] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<DropDownItem | null>(null);
  const [lastConfirmedOrder, setLastConfirmedOrder] = useState<DropDownItem | null>(null);
  const [doseInput, setDoseInput] = useState('');
  const [formInput, setFormInput] = useState('');
  const [isDistractorSectionOpen, setIsDistractorSectionOpen] = useState(true);
  const [simChartId, setSimChartId] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [doseOptions, setDoseOptions] = useState<string[]>([]);
  const [formOptions, setFormOptions] = useState<string[]>([]);
  const [pharmacyMetadata, setPharmacyMetadata] = useState(null);
  const [metadataMedicationOptions, setMetadataMedicationOptions] = useState<DropDownItem[]>([]);
  const [contentMedicationOptions, setContentMedicationOptions] = useState<DropDownItem[]>([]);

  useEffect(() => {
    const input = document.getElementById('distractor-dose') as HTMLInputElement | null;
    if (!input) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const isModifierCombo = e.ctrlKey || e.metaKey || e.altKey;
      const isNavigationOrControl = e.key.length > 1; // Backspace, Delete, ArrowLeft, Tab, Enter, Escape, etc.
      if (!isModifierCombo && !isNavigationOrControl && input.value.length >= 12) {
        e.preventDefault();
      }
    };
    input.addEventListener('keydown', handleKeyDown);
    return () => input.removeEventListener('keydown', handleKeyDown);
  }, [doseOptions]);

  useEffect(() => {
    const handleMouseDownCapture = (event: MouseEvent) => {
      if (!isDistractorSectionOpen) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      // keep focus while dragging in dropdown option
      const isInsideDistractorDropdown = !!target.closest(
        '#field-wrap-distractor-order-item .c-dropdown-search__option, #field-wrap-distractor-dose .c-dropdown-search__option, #field-wrap-distractor-form .c-dropdown-search__option'
      );

      if (isInsideDistractorDropdown) {
        event.preventDefault();
      }
    };

    const handleEnterKeyCapture = (event: KeyboardEvent) => {
      if (!isDistractorSectionOpen || event.key !== 'Enter') {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (!target || target.id !== 'distractor-order-item') {
        return;
      }

      const wrapper = document.getElementById('field-wrap-distractor-order-item');
      if (!wrapper) {
        return;
      }

      const isDropdownOpen = !!wrapper.querySelector('.c-dropdown-search--focus');
      const hasHighlightedOption = !!wrapper.querySelector('.c-dropdown-search__option-item--selected');

      // Keep the list open when Enter is pressed on free-typed text.
      if (isDropdownOpen && !hasHighlightedOption) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('mousedown', handleMouseDownCapture, true);
    document.addEventListener('keyup', handleEnterKeyCapture, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDownCapture, true);
      document.removeEventListener('keyup', handleEnterKeyCapture, true);
    };
  }, [isDistractorSectionOpen]);

  useEffect(() => {
    const loadData = async () => {
      if (!assignmentId) {
        setLoading(false);
        setInfoMessage('Barcode data is unavailable for this assignment until it is saved.');
        return;
      }

      try {
        if (!userId) {
          throw new Error('Missing instructor user id');
        }

        const instructorSummary = await assignmentService.getInstructorDetails(assignmentId, userId);
        const chartId = instructorSummary?.data?.simChartId;
        if (!chartId) {
          setInfoMessage('Barcode data is unavailable for this assignment.');
          setLoading(false);
          return;
        }

        setSimChartId(chartId);

        const [fragmentsResponse, directContentAllergies, directContentMedicationOptions, simulationContentResponse] = await Promise.all([
          chartService.loadFragments({
            chartId,
            navIds: [NAV_ID.PHARMACY, NAV_ID.PATIENT],
            fragmentTypes: [FragmentType.AUTHORED, FragmentType.CHARTING]
          }),
          loadDirectContentAllergies(
            simulationEhrId,
            simulationService.getSimulation,
            simulationService.getContentPhaseChartData
          ),
          loadDirectContentMedicationOptions(
            simulationEhrId,
            simulationService.getSimulation,
            simulationService.getContentPhaseChartData
          ),
          simulationEhrId ? simulationService.getSimulation(simulationEhrId).catch(() => null) : Promise.resolve(null)
        ]);

        setContentMedicationOptions(directContentMedicationOptions);

        const simulationAge = simulationContentResponse?.data?.patient?.age as number | undefined;

        const mainFragments = (fragmentsResponse?.data || []) as BarcodeFragment[];
        const normalized = normalizeFromFragments(mainFragments);
        const patientAllergiesFromChart = normalized.patient.allergies !== 'NA' ? normalized.patient.allergies : '';
        const resolvedAllergies = patientAllergiesFromChart || directContentAllergies || 'NA';
        const normalizedWithAllergies = {
          ...normalized,
          patient: { ...normalized.patient, allergies: resolvedAllergies }
        };
        const patientFragment = mainFragments.find((item) => item?.navElementId === NAV_ID.PATIENT);
        const rawPatientChartData = (patientFragment?.chartData || {}) as Record<string, unknown>;
        const detectedDobKey = Object.keys(rawPatientChartData).find((key) => /(date.*birth|dob)/i.test(key)) || 'patientDateOfBirth';
        const savedDob = typeof rawPatientChartData[detectedDobKey] === 'string' ? String(rawPatientChartData[detectedDobKey]) : '';

        if (!normalizedWithAllergies.medications.length) {
          setInfoMessage('Barcode data is unavailable for this assignment.');
        }

        const resolvedDob = normalizedWithAllergies.patient.dateOfBirth || '';
        const now = new Date();
        // Parse the stored ISO DOB in local time (matching the EHR ribbon) so the day-of-month agrees, then apply
        // the SIMSNG-5820 age recalculation on that local date (SIMSNG-5892).
        const localBirthday = resolvedDob ? formatDate({ date: toMoment(resolvedDob).toDate(), locale: Locales.EN_US }) : '';
        const recalculatedDob = simulationAge != null && localBirthday
          ? getSimulationEhrDateOfBirth(localBirthday, now, Locales.EN_US, now, simulationAge)
          : localBirthday;
        const normalizedDob = normalizeMonthDayDob(recalculatedDob, now.getFullYear());
        const resolvedDobIso = toIsoDob(normalizedDob.full);

        const patientContext = buildPharmacyPatientContext(rawPatientChartData, detectedDobKey, resolvedDobIso || savedDob || resolvedDob);
        chartService.fetchChartMetadata(NAV_ID.PHARMACY, patientContext)
          .then((response) => {
            setPharmacyMetadata(response?.data || null);
            const options = extractMedicationOptionsFromMetadata(response?.data?.chartFieldContentSet || []);
            setMetadataMedicationOptions(options);
          })
          .catch(() => {
            setPharmacyMetadata(null);
            setMetadataMedicationOptions([]);
          });

        setDetails({
          ...normalizedWithAllergies,
          patient: {
            ...normalizedWithAllergies.patient,
            age: resolvedDobIso ? calculateAge(resolvedDobIso) : normalizedWithAllergies.patient.age,
            // Same day-of-month as the ribbon, displayed in slash MM/DD/YYYY for the barcode required format
            dateOfBirth: normalizedDob.full
          }
        });
      } catch (_error) {
        setErrorMessage('Unable to retrieve medications for barcode details. You can close this modal and try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [assignmentId, userId, simulationEhrId]);

  const closeModal = () => modalService?.closeModal?.(modalId);

  const handleMedicationSelect = (medicationId: string, checked: boolean) => {
    setDetails((prevState) => ({
      ...prevState,
      medications: prevState.medications.map((item) => (item.id === medicationId ? { ...item, selected: checked } : item))
    }));
  };

  const medicationOptions: DropDownItem[] = useMemo(() => {
    if (metadataMedicationOptions.length) {
      return metadataMedicationOptions;
    }

    const pharmacyOrderMap = new Map<string, DropDownItem>();

    contentMedicationOptions.forEach((item) => {
      const displayName = normalizeMedicationOptionLabel(item.name || item.value);
      const identity = normalizeMedicationIdentity(displayName);
      if (!identity || pharmacyOrderMap.has(identity)) {
        return;
      }
      pharmacyOrderMap.set(identity, {
        id: item.id,
        name: displayName,
        value: displayName
      });
    });

    details.medications
      .filter((item) => !item.isDistractor)
      .forEach((item) => {
        const displayName = normalizeMedicationOptionLabel(item.medicationName);
        const identity = normalizeMedicationIdentity(displayName);
        if (!identity || pharmacyOrderMap.has(identity)) {
          return;
        }

        pharmacyOrderMap.set(identity, {
          id: item.id,
          name: displayName,
          value: displayName
        });
      });

    return Array.from(pharmacyOrderMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [contentMedicationOptions, details.medications, metadataMedicationOptions]);

  const filteredMedicationOptions = useMemo(
    () => medicationOptions.filter((option) => option.name.toLowerCase().includes(searchInput.toLowerCase())),
    [medicationOptions, searchInput]
  );

  const handleAddDistractorMedication = async () => {
    const orderName = selectedOrder?.name || searchInput.trim();
    if (!orderName || !doseInput.trim() || !formInput.trim()) {
      return;
    }

    const barcodeValue = generateBarcodeValue(orderName, doseInput.trim(), formInput.trim());
    const chartData = {
      name: orderName,
      dose: doseInput.trim(),
      form: formInput.trim(),
      [BARCODE_CHART_PROP]: barcodeValue,
      isDistractor: true
    };

    let fragmentId = `distractor-${Date.now()}`;

    if (simChartId) {
      try {
        const response = await chartService.saveFragment(simChartId, NAV_ID.PHARMACY, {
          navElementId: NAV_ID.PHARMACY,
          chartData,
          fragmentType: FragmentType.AUTHORED,
          chartingAt: new Date().toISOString(),
          active: true
        });
        if (response?.data?.fragmentId) {
          fragmentId = response.data.fragmentId;
        }
      } catch (_error) {
        // Fragment saved locally even if backend fails
      }
    }

    const distractorMedication: MedicationItem = {
      id: fragmentId,
      medicationName: orderName,
      dose: doseInput.trim(),
      form: formInput.trim(),
      barcodeValue,
      selected: false,
      isDistractor: true
    };

    setDetails((prevState) => ({
      ...prevState,
      medications: [...prevState.medications, distractorMedication]
    }));
    setSelectedOrder(null);
    setLastConfirmedOrder(null);
    setSearchInput('');
    setDoseInput('');
    setFormInput('');
    setDoseOptions([]);
    setFormOptions([]);
  };

  const handleClearDistractorInputs = () => {
    setSelectedOrder(null);
    setLastConfirmedOrder(null);
    setSearchInput('');
    setDoseInput('');
    setFormInput('');
    setDoseOptions([]);
    setFormOptions([]);
  };

  const extractDosesFromOrderLabel = (label: string): string[] => {
    const normalizedLabel = String(label || '').trim();
    if (!normalizedLabel) {
      return [];
    }

    const matches = normalizedLabel.match(/\d+(?:\.\d+)?\s*(?:mcg|mg|g|kg|ml|mL|l|L|units?|iu)(?:\s*\/\s*\d+(?:\.\d+)?\s*(?:ml|mL|l|L))?/gi);
    return matches ? matches.map((m) => m.replace(/\s+/g, ' ').trim()) : [];
  };

  const applyOrderSelection = (option: DropDownItem) => {
    setSelectedOrder(option);
    // Keep dropdown list re-openable with full options after a selection.
    setSearchInput('');

    const selectedLabel = option?.name || option?.value || '';
    const selectedIdentity = normalizeMedicationIdentity(selectedLabel);
    let matched = details.medications
      .filter((item) => !item.isDistractor)
      .filter((item) => normalizeMedicationIdentity(item.medicationName) === selectedIdentity);

    if (!matched.length) {
      matched = details.medications
        .filter((item) => !item.isDistractor)
        .filter((item) => {
          const medicationIdentity = normalizeMedicationIdentity(item.medicationName);
          if (!medicationIdentity || !selectedIdentity) {
            return false;
          }
          return selectedIdentity.includes(medicationIdentity) || medicationIdentity.includes(selectedIdentity);
        });
    }

    const matchedMedicationName = matched[0]?.medicationName || '';
    const metadataDoseOptions = getPharmacyMetadataOptionsForField(
      pharmacyMetadata,
      'medicationDose',
      option?.id,
      selectedLabel,
      'visible'
    ).map(validateMedicationDoseInput);
    const metadataFormOptions = getPharmacyMetadataOptionsForField(
      pharmacyMetadata,
      'medicationForm',
      option?.id,
      selectedLabel,
      'prefill'
    ).map(validateMedicationFormInput);
    const fallbackDoses = extractDosesFromOrderLabel(selectedLabel);
    const fallbackForm = extractMedicationFormFromOrderLabel(selectedLabel, matchedMedicationName);

    const uniqueDoses = metadataDoseOptions.length
      ? Array.from(new Set(metadataDoseOptions))
      : Array.from(
        new Set(
          matched
            .map((m) => validateMedicationDoseInput(String(m.dose || '').trim()))
            .filter((dose) => !!dose && dose !== '--')
        )
      );
    if (!uniqueDoses.length && fallbackDoses.length) {
      uniqueDoses.push(...fallbackDoses.map(validateMedicationDoseInput));
    }

    const uniqueForms = metadataFormOptions.length
      ? Array.from(new Set(metadataFormOptions))
      : Array.from(
        new Set(
          matched
            .map((m) => validateMedicationFormInput(String(m.form || '').trim()))
            .filter((form) => !!form)
        )
      );
    if (!uniqueForms.length && fallbackForm) {
      uniqueForms.push(validateMedicationFormInput(fallbackForm));
    }

    const selectableDoses = uniqueDoses.filter((dose) => dose !== 'Special Dose');

    setDoseOptions(selectableDoses);
    setFormOptions(uniqueForms);
    setDoseInput(selectableDoses.length === 1 ? selectableDoses[0] : '');
    setFormInput(uniqueForms.length === 1 ? uniqueForms[0] : '');
  };

  const handleDistractorOrderSelect = (option: DropDownItem) => {
    // library fires onOptionSelected(undefined) when Enter has no highlighted item,
    // and fires onOptionSelected({ value: text }) on blur — both lack a real option id
    if (!option?.id) {
      if (lastConfirmedOrder && searchInput.trim()) {
        applyOrderSelection(lastConfirmedOrder);
      }
      return;
    }

    setLastConfirmedOrder(option);
    applyOrderSelection(option);

    // event.preventDefault() in handleMouseDownCapture keeps focus on the input
    // after option selection, which prevents the `focus` event from firing again.
    setTimeout(() => {
      (document.getElementById('distractor-order-item') as HTMLInputElement)?.blur();
    }, 0);
  };

  const handleDistractorDoseSelect = (option: DropDownItem) => {
    if (!option?.id) {
      return;
    }

    setDoseInput(validateMedicationDoseInput(option?.value || ''));

    setTimeout(() => {
      (document.getElementById('distractor-dose') as HTMLInputElement)?.blur();
    }, 0);
  };

  const selectedMedications = details.medications.filter((item) => item.selected);
  const selectedPrimary = selectedMedications.filter((item) => !item.isDistractor);
  const selectedDistractors = selectedMedications.filter((item) => item.isDistractor);
  const selectedMedicationCount = selectedMedications.length;
  const primaryMedications = details.medications.filter((item) => !item.isDistractor);
  const distractorMedications = [...details.medications.filter((item) => item.isDistractor)].reverse();

  const handlePrintPreview = () => {
    setShowPrintPreview(true);
    setTimeout(() => {
      window.print();
      setShowPrintPreview(false);
    }, 500);
  };
  const toggleDistractorSection = () => setIsDistractorSectionOpen((prev) => !prev);

  const hasOrderInput = !!(selectedOrder?.name || searchInput.trim());
  const isAddDistractorDisabled = !hasOrderInput || !doseInput.trim() || !formInput.trim();
  const isClearDisabled = !hasOrderInput && !doseInput.trim() && !formInput.trim();

  return (
    <div className="barcode-modal" role="dialog" aria-modal="true" aria-label="Simulation Barcodes">
      <ELSFlex between middle className="barcode-modal__header">
        <h3>Simulation Barcodes</h3>
        <button className="barcode-modal__close" type="button" aria-label="Close" onClick={closeModal}>
          <ELSIcon name="close" prefix="hmds" color="secondary" size="1x" />
        </button>
      </ELSFlex>

      <div className="barcode-modal__body">
        {loading && <div className="barcode-modal__status">Loading barcode details...</div>}
        {!loading && errorMessage && <div className="barcode-modal__status barcode-modal__status--error">{errorMessage}</div>}
        {!loading && !errorMessage && infoMessage && <div className="barcode-modal__status barcode-modal__status--info">{infoMessage}</div>}

        {!loading && !errorMessage && (
          <>
            <div className="barcode-modal__status">
              <p>Patient ID Band</p>
            </div>

          <div className="barcode-modal__panel">

            <ELSFlex className="barcode-modal__patient-row" between>
              <ELSFlexItem className="barcode-modal__patient-details">
                <p>{`Name: ${details.patient.name}`}</p>
                <p>{`MRN: ${details.patient.mrn}`}</p>
                <p>{`Age: ${details.patient.age}`} Y</p>
                {details.patient.dateOfBirth && <p>{`Date of Birth: ${details.patient.dateOfBirth}`}</p>}
                <p>{`Patient ID: ${details.patient.patientId}`}</p>
                <p>{`Allergies: ${details.patient.allergies || 'NA'}`}</p>
              </ELSFlexItem>
              <ELSFlexItem className="barcode-modal__patient-qr">
                {details.patient.patientId && details.patient.patientId !== '--' ? (
                  <div className="barcode-modal__data-matrix" aria-label={`Patient ID Data Matrix ${details.patient.patientId}`}>
                    <DataMatrixFromValue value={details.patient.patientId} className="barcode-modal__data-matrix-svg" />
                  </div>
                ) : (
                  <div className="barcode-modal__qr-placeholder" aria-label="Patient ID Data Matrix" />
                )}
              </ELSFlexItem>
            </ELSFlex>
          </div>

          <div className="barcode-modal__medications-header">
            <p className="barcode-modal__body-copy">Select the medications from the MAR that you want to print</p>
            <div className="barcode-modal__medications-list">
              {!primaryMedications.length && <div className="barcode-modal__empty-list">No medications available for barcode printing.</div>}
              {primaryMedications.map((item) => (
                <div key={item.id} className="barcode-modal__medication-item">
                  <ELSFlex between top>
                    <ELSFlexItem className="barcode-modal__medication-copy">
                      <ELSCheckBox
                        id={`barcode-medication-${item.id}`}
                        checked={item.selected}
                        changeHandler={(_event, checked: boolean) => handleMedicationSelect(item.id, checked)}
                        labelText={formatMedicationName(item)}
                      />
                      <div className="barcode-modal__dose">{`Dose: ${item.dose}`}</div>
                    </ELSFlexItem>
                    <ELSFlexItem className="barcode-modal__barcode-block">
                      <BarcodeFromValue value={item.barcodeValue} className="barcode-modal__barcode-image" />
                      <div className="barcode-modal__barcode-value">{item.barcodeValue}</div>
                    </ELSFlexItem>
                  </ELSFlex>
                </div>
              ))}
            </div>
          </div>

          <div className="barcode-modal__distractor-panel">
            <button
              type="button"
              className="barcode-modal__accordion-toggle"
              onClick={toggleDistractorSection}
              aria-expanded={isDistractorSectionOpen}
            >
              <ELSIcon name={isDistractorSectionOpen ? 'chevron-up' : 'chevron-down'} prefix="hmds" color="secondary" size="1x" />
              <span className="barcode-modal__accordion-title">Create Labels for Distracter Medication</span>
            </button>

            {isDistractorSectionOpen && (
              <div className="barcode-modal__distractor-content">
                <div className="barcode-modal__order-item-title">Order Item Details</div>
                <div className="barcode-modal__distractor-grid">
                  <div className="barcode-modal__distractor-item barcode-modal__distractor-item--order">
                    <label htmlFor="distractor-order-item" className="barcode-modal__input-label">
                      <span className="barcode-modal__required">*</span> Order Item
                    </label>
                    <ELSDropDownSearch
                      disableAutoComplete
                      id="distractor-order-item"
                      name="distractorOrderItem"
                      onOptionSelected={handleDistractorOrderSelect}
                      onSearchChangeHandler={(value: string) => {
                        if (selectedOrder) {
                          setLastConfirmedOrder(selectedOrder);
                        }
                        setSelectedOrder(null);
                        setSearchInput(value);
                        setDoseOptions([]);
                        setFormOptions([]);
                        setDoseInput('');
                        setFormInput('');
                      }}
                      options={filteredMedicationOptions}
                      placeholder="Search or Select"
                      showSearchIcon
                      type="normal"
                      value={selectedOrder?.name || searchInput}
                      ariaLabel="Order Item"
                    />
                  </div>

                  <div className="barcode-modal__distractor-item barcode-modal__distractor-item--dose">
                    <label htmlFor="distractor-dose" className="barcode-modal__input-label">
                      <span className="barcode-modal__required">*</span> Medication Dose
                    </label>
                    {doseOptions.length > 1 ? (
                      <ELSDropDownSearch
                        disableAutoComplete
                        id="distractor-dose"
                        name="distractorDose"
                        onOptionSelected={handleDistractorDoseSelect}
                        onSearchChangeHandler={(value: string) => setDoseInput(validateMedicationDoseInput(value))}
                        options={doseOptions.map((d, i) => ({ id: `dose-opt-${i}`, name: d, value: d }))}
                        placeholder="Select Medication Dose"
                        type="normal"
                        value={doseInput}
                        ariaLabel="Medication Dose"
                      />
                    ) : (
                      <ELSTextBox
                        id="distractor-dose"
                        name="distractorDose"
                        value={doseInput}
                        changeHandler={({ target }) => setDoseInput(validateMedicationDoseInput(target.value))}
                        placeholder="Enter Medication Dose"
                      />
                    )}
                  </div>

                  <div className="barcode-modal__distractor-item barcode-modal__distractor-item--form">
                    <label htmlFor="distractor-form" className="barcode-modal__input-label">
                      <span className="barcode-modal__required">*</span> Medication Form
                    </label>
                    {formOptions.length >= 1 ? (
                      <ELSDropDownSearch
                        disableAutoComplete
                        id="distractor-form"
                        name="distractorForm"
                        onOptionSelected={(option: DropDownItem) => setFormInput(validateMedicationFormInput(option?.value || ''))}
                        onSearchChangeHandler={(value: string) => setFormInput(validateMedicationFormInput(value))}
                        options={formOptions.map((f, i) => ({ id: `form-opt-${i}`, name: f, value: f }))}
                        placeholder="Select Medication Form"
                        type="normal"
                        value={formInput}
                        ariaLabel="Medication Form"
                      />
                    ) : (
                      <ELSTextBox
                        id="distractor-form"
                        name="distractorForm"
                        value={formInput}
                        changeHandler={({ target }) => setFormInput(validateMedicationFormInput(target.value))}
                        placeholder="Enter Medication Form"
                        isDisabled={!selectedOrder?.name && !searchInput.trim()}
                      />
                    )}
                  </div>
                </div>

                <div className="barcode-modal__distractor-actions">
                  <ELSButton text="Add Medication" type="primary" onClick={handleAddDistractorMedication} isDisabled={isAddDistractorDisabled} />
                  <ELSButton text="Clear" type="secondary" onClick={handleClearDistractorInputs} isDisabled={isClearDisabled} className="u-els-margin-left" />
                </div>
              </div>
            )}
          </div>

          {!!distractorMedications.length && (
            <div className="barcode-modal__medications-list barcode-modal__distractor-list">
              {distractorMedications.map((item) => (
                <div key={item.id} className="barcode-modal__medication-item">
                  <ELSFlex between top>
                    <ELSFlexItem className="barcode-modal__medication-copy">
                      <ELSCheckBox
                        id={`barcode-medication-${item.id}`}
                        checked={item.selected}
                        changeHandler={(_event, checked: boolean) => handleMedicationSelect(item.id, checked)}
                        labelText={formatMedicationName(item)}
                      />
                      <div className="barcode-modal__dose">{`Dose: ${item.dose}`}</div>
                    </ELSFlexItem>
                    <ELSFlexItem className="barcode-modal__barcode-block">
                      <BarcodeFromValue value={item.barcodeValue} className="barcode-modal__barcode-image" />
                      <div className="barcode-modal__barcode-value">{item.barcodeValue}</div>
                    </ELSFlexItem>
                  </ELSFlex>
                </div>
              ))}
            </div>
          )}

          <ELSFlex className="barcode-modal__footer">
            <ELSButton
              type="primary"
              text="Print Preview"
              isDisabled={selectedMedicationCount === 0}
              onClick={handlePrintPreview}
            />
          </ELSFlex>
          </>
        )}
      </div>

      {showPrintPreview && (
        <BarcodePrintPreview
          selectedPrimary={selectedPrimary}
          selectedDistractors={selectedDistractors}
          patient={details.patient}
          renderBarcode={(value, className) => <BarcodeFromValue value={value} className={className} />}
          renderDataMatrix={(value, className) => <DataMatrixFromValue value={value} className={className} />}
        />
      )}
    </div>
  );
};

interface OpenBarcodeModalProps {
  modalService: ELSModalServiceType;
  assignmentId?: number;
  simulationEhrId?: string;
  userId?: number;
}

export const openBarcodeModal = ({ modalService, assignmentId, simulationEhrId, userId }: OpenBarcodeModalProps) => {
  modalService.openModal({
    modalId: BARCODE_MODAL_ID,
    color: 'primary',
    showClose: false,
    content: (
      <BarcodeModalContent
        assignmentId={assignmentId}
        simulationEhrId={simulationEhrId}
        userId={userId}
        modalService={modalService}
        modalId={BARCODE_MODAL_ID}
      />
    )
  });
};
